import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGadgetUpdateFacade } from 'redux/gadget-update/gadget-update.facade';
import { useLayoutUpdateFacade } from 'redux/layout-update/layout-update.facade';
import { useProjectReadFacade } from 'redux/project-read/project-read.facade';
import {
  GadgetCommonModelGadgetTypeCodeEnum,
  GadgetEditModel,
  LayoutEntry,
  LayoutResponse,
  RoleModel,
} from 'types/entity';
import { layoutEditToEntry } from 'utils/converter/layout';
import { newGadgetEdit, newGadgetEditByCriteria } from 'utils/converter/gadget';
import { useSearchCriteriaReadFacade } from 'redux/search-criteria-read/search-criteria-read.facade';
import { Layout } from 'react-grid-layout';
import { FormValues as GadgetNameFormValues } from 'components/molecules/dialog/FpEditGadgetNameDialog';
import { GadgetColorType } from 'types/constants/gadget-color';
import { useUnusedRoleReadFacade } from 'redux/unused-role-read/unused-role-read.facade';
import { useMainMenuUpdateFacade } from 'redux/main-menu-update/main-menu-update.facade';

/**
 * レイアウトの保存に関するアクション
 */
export const useSaveableActions = () => {
  const { selectedProject } = useProjectReadFacade();
  const { selectedLayout, saveLayout, unTouched: unTouchedByLayout } = useLayoutUpdateFacade();
  const { mainMenuMap, unApplied } = useMainMenuUpdateFacade();
  const { unTouched: unTouchedByGadget } = useGadgetUpdateFacade();

  // レイアウト変更を実行する
  const onSaveLayout = (): Promise<void> => {
    const layout: LayoutEntry = layoutEditToEntry(selectedLayout, mainMenuMap);

    return saveLayout({
      projectId: selectedProject.project_id || '',
      layoutId: selectedLayout.layout_id || '',
      layout,
    }).then(() => {
      unApplied();
      unTouchedByGadget();
      unTouchedByLayout();
    });
  };

  return {
    onSaveLayout,
  };
};

/**
 * レイアウト編集画面の状態に関するアクション
 */
export const useLayoutEditState = () => {
  const {
    processing, touched: layoutTouched, selectedLayout, clear,
  } = useLayoutUpdateFacade();
  const { touched: gadgetTouched } = useGadgetUpdateFacade();
  const { applied, clearAll } = useMainMenuUpdateFacade();

  // 複数のStoreを参照して保存できるか判定する
  const [saveable, setSaveable] = useState(false);
  useEffect(() => {
    setSaveable(
      (layoutTouched || gadgetTouched || applied)
      && !processing
      && !!selectedLayout.gadget?.length,
    );
  }, [layoutTouched, gadgetTouched, processing, setSaveable, selectedLayout, applied]);

  // 画面の編集中か
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    setEditing(gadgetTouched || layoutTouched || applied);
  }, [setEditing, applied, layoutTouched, gadgetTouched]);

  // レイアウト編集を終了する
  const exitLayoutEdit = () => {
    clear();
    clearAll();
  };

  return {
    saveable,
    editing,
    exitLayoutEdit,
  };
};

/**
 * 役割変更に関するアクション
 */
export const useRoleActions = () => {
  const { update } = useUnusedRoleReadFacade();
  const { selectedLayout, updateRoles } = useLayoutUpdateFacade();
  const [displayRoleViewer, setDisplayRoleViewer] = useState<boolean>(false);
  const [displayRoleChanger, setDisplayRoleChanger] = useState<{show: boolean, mode: 'PROJECT'|'HEADQUARTERS'}>({ show: false, mode: 'PROJECT' });

  // レイアウトを変更する
  const onChangeRoles = (
    unusedRoles: RoleModel[],
    layoutRoles: RoleModel[],
  ) => {
    updateRoles(selectedLayout.layout_id || '', layoutRoles);
    update(unusedRoles);
    onCloseRoleChanger();
  };

  // 役割を変更しようとする
  const onShowRoleChanger = (mode: 'PROJECT'|'HEADQUARTERS') => {
    setDisplayRoleChanger({ show: true, mode });
  };
  // ダイアログを閉じる
  const onCloseRoleChanger = () => {
    setDisplayRoleChanger((pre) => ({ show: false, mode: pre.mode }));
  };

  // 役割一覧表示
  const onShowRoleViewer = () => {
    setDisplayRoleViewer(true);
  };
  // ダイアログを閉じる
  const onCloseRoleViewer = () => {
    setDisplayRoleViewer(false);
  };

  return {
    displayRoleChanger,
    onChangeRoles,
    onShowRoleChanger,
    onCloseRoleChanger,
    displayRoleViewer,
    onShowRoleViewer,
    onCloseRoleViewer,
  };
};

/**
 * プレビュー表示に関するアクション
 */
export const usePreviewActions = () => {
  const navigate = useNavigate();

  const onPreview = () => {
    navigate('/admin/layout/preview');
  };

  return {
    onPreview,
  };
};

/**
 * キャンセルに関するアクション
 */
export const useCancelActions = () => {
  const navigate = useNavigate();

  const onCancel = () => {
    navigate('/admin/layout_role_assign');
  };

  return {
    onCancel,
  };
};

/**
 * レイアウト切り替えに関するアクション
 */
export const useLayoutSwitchActions = () => {
  const { selectLayout } = useLayoutUpdateFacade();
  const { onSaveLayout } = useSaveableActions();
  const { editing, exitLayoutEdit } = useLayoutEditState();

  const [switchingLayoutId, setSwitchingLayoutId] = useState<string>();

  // レイアウトを切り替える
  const onChangeLayout = (layout: LayoutResponse) => {
    if (editing) {
      setSwitchingLayoutId(layout.layout_id || '');
      return;
    }
    selectLayout(layout.layout_id || '');
  };
  // 編集中のレイアウトを保存して切り替える
  const onConfirmSwitch = (layoutId: string) => {
    onSaveLayout().then(() => {
      exitLayoutEdit();
      selectLayout(layoutId);
      onResetSwitching();
    });
  };
  // レイアウトを変更しようとする状態の初期化
  const onResetSwitching = () => {
    setSwitchingLayoutId(undefined);
  };

  return {
    switchingLayoutId,
    onChangeLayout,
    onConfirmSwitch,
    onResetSwitching,
  };
};

/**
 * 検索条件に関するアクション
 */
export const useSearchCriteriaActions = () => {
  const navigate = useNavigate();

  const { onSaveLayout } = useSaveableActions();
  const { editing, exitLayoutEdit } = useLayoutEditState();

  const [displayToSearchConfirmDialog, setDisplayToSearchConfirmDialog] = useState<boolean>(false);

  // 検索条件追加時の操作
  const onCloseDisplayToSearchConfirmDialog = () => {
    setDisplayToSearchConfirmDialog(false);
  };
  // 遷移していいか確認しようとする
  const onShowDisplayToSearchConfirmDialog = () => {
    if (editing) {
      setDisplayToSearchConfirmDialog(true);
      return;
    }
    toSearch();
  };
  // 検索画面へ遷移する
  const toSearch = async () => {
    let navigatable = true;
    // レイアウトを保存する
    if (editing) {
      await onSaveLayout()
        .catch(() => {
          navigatable = false;
        });
    }
    // 遷移可能か判定する
    if (!navigatable) {
      onCloseDisplayToSearchConfirmDialog();
      return;
    }
    navigate('/search');
    exitLayoutEdit();
  };

  return {
    displayToSearchConfirmDialog,
    toSearch,
    onShowDisplayToSearchConfirmDialog,
    onCloseDisplayToSearchConfirmDialog,
  };
};

/**
 * ガジェットの追加に関するアクション
 */
export const useGadgetAddActions = () => {
  const { criterias } = useSearchCriteriaReadFacade();
  const { selectedLayout, updateGadgetRef } = useLayoutUpdateFacade();
  const { upsertGadget } = useGadgetUpdateFacade();
  const { addEmptyMainMenu } = useMainMenuUpdateFacade();

  // ガジェットを追加する
  const onAddGadget = (gadgetContentsId: string, gadgetTypeCode: GadgetCommonModelGadgetTypeCodeEnum) => {
    const newest = newGadgetEdit(gadgetContentsId, gadgetTypeCode);
    onUpdateLauoutAndAddGadget(newest);
  };
  // メインメニューガジェットを追加する
  const onAddMainMenuGadget = (gadgetContentsId: string, gadgetTypeCode: GadgetCommonModelGadgetTypeCodeEnum) => {
    const newest = newGadgetEdit(gadgetContentsId, gadgetTypeCode);
    onUpdateLauoutAndAddGadget(newest);
    addEmptyMainMenu(gadgetContentsId);
  };
  // 検索条件ガジェットを追加する
  const onAddSearchCriteriaGadget = (gadgetContentsId: string, gadgetTypeCode: GadgetCommonModelGadgetTypeCodeEnum) => {
    const newest = newGadgetEditByCriteria(gadgetContentsId, gadgetTypeCode, criterias);
    onUpdateLauoutAndAddGadget(newest);
  };
  // ガジェット追加の共通処理
  const onUpdateLauoutAndAddGadget = (newest: GadgetEditModel) => {
    upsertGadget(newest);
    // ガジェットを追加しただけではレイアウトに変更が反映されないので、紐付けも更新する
    updateGadgetRef({
      ...selectedLayout,
      gadget: [
        ...selectedLayout.gadget || [],
        newest,
      ],
    });
  };

  return {
    onAddGadget,
    onAddMainMenuGadget,
    onAddSearchCriteriaGadget,
  };
};

/**
 * ガジェットの更新に関するアクション
 */
export const useGadgetUpdateActions = () => {
  const navigate = useNavigate();
  const { selectedLayout, updateGadgetRef } = useLayoutUpdateFacade();
  const {
    upsertManyGadget, upsertGadget, removeManyGadget, selectGadget, selectedGadget,
  } = useGadgetUpdateFacade();
  const { startEdit } = useMainMenuUpdateFacade();

  // ガジェットの位置、サイズ変更、削除
  const onChangePositionAndSizeAndDelete = (layouts: Layout[]) => {
    // 存在しないと思うが、レイアウトを選択してなかったら終了する
    if (!selectedLayout || !selectedLayout.gadget) { return; }

    const removed: string[] = [];
    const changed: GadgetEditModel[] = [];

    selectedLayout?.gadget.forEach((g) => {
      const newXGadget: Layout | undefined = layouts.find((xGadget) => xGadget.i === g.gadget_id);
      newXGadget
        // 変更後のリストから見つからなかったら、削除されたとみなす
        ? changed.push({
          ...g,
          x_coordinates: newXGadget?.x,
          y_coordinates: newXGadget?.y,
          width: newXGadget?.w,
          height: newXGadget?.h,
        })
        // 変更後のリストから見つかったら、変更されたとみなす
        : removed.push(g.gadget_id || '');
    });
    // ガジェット変更
    upsertManyGadget(changed);
    // ガジェット削除
    if (removed.length) {
      removeManyGadget(removed);
      // レイアウトの紐付け更新
      updateGadgetRef({
        ...selectedLayout,
        gadget: selectedLayout.gadget
          .filter((g) => !removed.includes(g.gadget_id || '')),
      });
    }
  };

  const [displayGadgetNameDialog, setDisplayGadgetNameDialog] = useState<boolean>(false);

  // ガジェット名変更ダイアログの表示
  const onShowGdagetNameDialog = (gadget_id: string) => {
    selectGadget(gadget_id);
    setDisplayGadgetNameDialog(true);
  };
  // ガジェット名変更ダイアログを閉じる
  const onCloseGdagetNameDialog = () => {
    setDisplayGadgetNameDialog(false);
  };
  // ガジェット名変更の実行
  const onChangeGadgetName = (gadgetId: string, values: GadgetNameFormValues) => {
    if (gadgetId !== selectedGadget?.gadget_id) {
      return;
    }
    const gadgetModel: GadgetEditModel = {
      ...selectedGadget,
      display_name_ja: values.display_name_ja || '',
      display_name_en: values.display_name_en,
    };
    upsertGadget(gadgetModel);
    setDisplayGadgetNameDialog(false);
  };

  const [displayGadgetImageDialog, setDisplayGadgetImageDialog] = useState<boolean>(false);

  // ガジェット背景色変更ダイアログの表示
  const onShowGadgetImageDialog = (gadget_id: string) => {
    selectGadget(gadget_id);
    setDisplayGadgetImageDialog(true);
  };
  // ガジェット背景色変更ダイアログを閉じる
  const onCloseGadgetImageDialog = () => {
    setDisplayGadgetImageDialog(false);
  };
  // ガジェット背景色変更を実行する
  const onChangeGadgetImage = (gadgetId: string, color: GadgetColorType, imageUrl: string | undefined) => {
    if (gadgetId !== selectedGadget?.gadget_id) {
      return;
    }
    const gadgetModel: GadgetEditModel = {
      ...selectedGadget,
      background_color: color,
      background_image_url: imageUrl,
    };
    upsertGadget(gadgetModel);
    setDisplayGadgetImageDialog(false);
  };

  // メインメニュー変更画面に遷移する
  const onNavigateMaunMenuSetting = (gadgetContentsId: string) => {
    startEdit(gadgetContentsId);
    navigate('/admin/layout/edit/main_menu');
  };

  return {
    onChangePositionAndSizeAndDelete,
    displayGadgetNameDialog,
    onShowGdagetNameDialog,
    onCloseGdagetNameDialog,
    onChangeGadgetName,
    displayGadgetImageDialog,
    onShowGadgetImageDialog,
    onCloseGadgetImageDialog,
    onChangeGadgetImage,
    onNavigateMaunMenuSetting,
  };
};

/**
 * 本社支店役割、またはPJ役割を抽出するフィルタを返す
 * @param mode PROJECT: PJ役割, HEADQUARTERS: 本社支店役割
 */
export const roleFilterBuilder = (mode: 'PROJECT' | 'HEADQUARTERS') => (role: RoleModel): boolean => {
  const isHeadQuoterRole = role.role_code.startsWith('XR') || role.role_code.startsWith('XSYS');
  switch (mode) {
    case 'PROJECT': return !isHeadQuoterRole;
    case 'HEADQUARTERS': return isHeadQuoterRole;
    default: return false;
  }
};
